import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, Brain } from 'lucide-react';

/**
 * Privacy Policy - public route at /privacy.
 *
 * Drafted for Google Play Store submission compliance (Data Safety form
 * + Privacy Policy URL requirement) and GDPR / CCPA basics. Tailored to
 * BrainSync's actual data flows: Google OAuth, Stripe Web Billing,
 * RevenueCat, Claude / Whisper LLM processing, Emergent Object Storage.
 *
 * NOT legal advice - encourage the user to have a lawyer review before
 * launch if they're worried about edge cases.
 */
export default function PrivacyPolicy() {
    useEffect(() => {
        // Static page - paint immediately, no auth waits needed.
        window.__brainsyncDismissSplash?.();
        // If the URL carries a hash (e.g. /privacy#data-deletion - used
        // by Google Play Data Safety links and external linkbacks),
        // scroll to that section. Otherwise scroll to top so deep
        // refreshes don't leave the user mid-document.
        const hash = (typeof window !== 'undefined' && window.location.hash) || '';
        if (hash) {
            // Defer one frame so the DOM is mounted before we measure.
            requestAnimationFrame(() => {
                const id = hash.replace('#', '');
                const el = document.getElementById(id);
                if (el) {
                    el.scrollIntoView({ behavior: 'smooth', block: 'start' });
                    return;
                }
                window.scrollTo(0, 0);
            });
        } else {
            window.scrollTo(0, 0);
        }
    }, []);

    const lastUpdated = 'May 28, 2026';

    return (
        <div className="min-h-screen bg-background" data-testid="privacy-policy-page">
            {/* Header */}
            <header className="sticky top-0 z-30 bg-background/95 backdrop-blur border-b border-border">
                <div className="max-w-3xl mx-auto px-6 py-4 flex items-center gap-3">
                    <Link
                        to="/"
                        className="flex items-center gap-1.5 text-sm font-medium text-muted-foreground hover:text-foreground transition-colors"
                        data-testid="privacy-back-link"
                    >
                        <ArrowLeft className="w-4 h-4" /> Back
                    </Link>
                    <div className="flex-1" />
                    <div className="flex items-center gap-1.5">
                        <Brain className="w-4 h-4 text-primary" />
                        <span className="text-sm font-bold text-foreground">BrainSync</span>
                    </div>
                </div>
            </header>

            <main className="max-w-3xl mx-auto px-6 py-10">
                <h1 className="text-4xl font-bold text-foreground mb-2">Privacy Policy</h1>
                <p className="text-sm text-muted-foreground mb-8">Last updated: {lastUpdated}</p>

                <div className="prose prose-sm max-w-none text-foreground space-y-6 leading-relaxed">
                    <section>
                        <p>
                            This Privacy Policy describes how <strong>BrainSync</strong> ("we", "our", or "us") collects, uses, and shares
                            information when you use our mobile and web applications (collectively, the "Service") available at
                            <a href="https://brainsync.pro" className="text-primary hover:underline"> brainsync.pro</a> and on the Google Play Store
                            (package <code className="text-xs bg-muted px-1.5 py-0.5 rounded">com.brainsync.app</code>).
                        </p>
                        <p>
                            By using BrainSync, you agree to the collection and use of information in accordance with this policy.
                        </p>
                    </section>

                    <section>
                        <h2 className="text-2xl font-semibold mt-8 mb-3">1. Information We Collect</h2>

                        <h3 className="text-lg font-semibold mt-5 mb-2">1.1 Account information</h3>
                        <p>
                            When you sign in with Google, we receive your email address, name, and profile picture from your Google
                            account. We do not request access to your contacts, calendar, or any other Google service data.
                        </p>

                        <h3 className="text-lg font-semibold mt-5 mb-2">1.2 Content you save</h3>
                        <p>
                            When you save a bookmark, we store the URL you submit, the metadata we fetch from that URL (title, thumbnail,
                            description, author, publish date), and the AI-generated summary, tags, and entity data we produce.
                            For media content (YouTube, Facebook, Instagram, TikTok), we may transcribe the audio in order to generate the summary.
                            Transcripts are stored alongside the bookmark.
                        </p>

                        <h3 className="text-lg font-semibold mt-5 mb-2">1.3 Usage data</h3>
                        <p>
                            We log basic interaction events (bookmark saves, searches, shares, subscription changes) to operate the
                            Service and improve features. We do not use third-party analytics SDKs that track users across apps.
                        </p>

                        <h3 className="text-lg font-semibold mt-5 mb-2">1.4 Payment information</h3>
                        <p>
                            We do not store credit-card details. Payments are processed by Stripe and RevenueCat. We store a
                            customer identifier returned by these processors and your current subscription tier.
                        </p>

                        <h3 className="text-lg font-semibold mt-5 mb-2">1.5 Device and log data</h3>
                        <p>
                            Our servers log standard request information (IP address, user-agent, timestamp, path) for security and
                            debugging. Logs are retained for 30 days and are not used for advertising.
                        </p>
                    </section>

                    <section>
                        <h2 className="text-2xl font-semibold mt-8 mb-3">2. How We Use Your Information</h2>
                        <ul className="list-disc pl-6 space-y-1.5">
                            <li>To authenticate you and maintain your session</li>
                            <li>To process the URLs you save and generate AI summaries, tags, and entity extractions</li>
                            <li>To deliver the Service features you request (search, sharing, recommendations)</li>
                            <li>To process subscriptions and bill you for Premium tiers</li>
                            <li>To improve the AI prompts and quality (using aggregate, de-identified usage patterns only)</li>
                            <li>To respond to your support requests</li>
                            <li>To detect and prevent abuse, fraud, or security incidents</li>
                        </ul>
                    </section>

                    <section>
                        <h2 className="text-2xl font-semibold mt-8 mb-3">3. AI Processing Disclosure</h2>
                        <p>
                            When you save content, we send the page content and/or media transcript to large-language-model providers
                            (currently Anthropic Claude for summarization and OpenAI Whisper for transcription) to generate the AI
                            summary, tags, and entity data. These providers process the content under their own privacy terms and
                            do not, per their policies, use API data to train their public models.
                        </p>
                        <p>
                            We do not sell your content to any third party and we do not use your bookmarks to train any model
                            other than internal quality / prompt tuning using aggregate, de-identified samples.
                        </p>
                    </section>

                    <section>
                        <h2 className="text-2xl font-semibold mt-8 mb-3">4. Sharing</h2>
                        <p>
                            We share your information only with the following categories of service providers, each bound by a
                            data-processing agreement:
                        </p>
                        <ul className="list-disc pl-6 space-y-1.5">
                            <li><strong>Google</strong> - authentication (Google Sign-In)</li>
                            <li><strong>Anthropic</strong> - Claude API for summarization and entity extraction</li>
                            <li><strong>OpenAI</strong> - Whisper API for audio transcription</li>
                            <li><strong>Stripe</strong> - payment processing</li>
                            <li><strong>RevenueCat</strong> - subscription state management</li>
                            <li><strong>Emergent</strong> - hosting and object storage for media thumbnails / transcripts</li>
                            <li><strong>MongoDB Atlas</strong> - primary database</li>
                            <li><strong>Cloudflare</strong> - CDN and DDoS protection</li>
                        </ul>
                        <p>
                            We also share information when required by law, when necessary to enforce our Terms, or to protect
                            the rights, property, or safety of BrainSync, our users, or others.
                        </p>
                        <p>
                            <strong>We do not sell your personal information.</strong>
                        </p>
                    </section>

                    <section>
                        <h2 className="text-2xl font-semibold mt-8 mb-3">5. Public Sharing of Bookmarks</h2>
                        <p>
                            BrainSync lets you mark bookmarks and collections as <em>Unlisted</em> (anyone with the link can view) or
                            <em> Public</em> (anyone can find them). Content you mark as Private is only visible to your account.
                            When you share a link via the in-app share sheet, the bookmark is automatically upgraded to Unlisted.
                            You can change the visibility back to Private at any time from the bookmark's Share menu.
                        </p>
                    </section>

                    <section>
                        <h2 className="text-2xl font-semibold mt-8 mb-3">6. Data Retention</h2>
                        <ul className="list-disc pl-6 space-y-1.5">
                            <li>Bookmarks and AI-generated data - kept until you delete the bookmark or your account</li>
                            <li>Request logs - 30 days</li>
                            <li>Backups - 30 days</li>
                            <li>Payment-processor data - per Stripe and RevenueCat retention policies (typically 7 years for tax records)</li>
                        </ul>
                    </section>

                    <section id="data-deletion">
                        <h2 className="text-2xl font-semibold mt-8 mb-3">7. Your Rights &amp; Account Deletion</h2>
                        <p>Depending on your jurisdiction, you have the following rights:</p>
                        <ul className="list-disc pl-6 space-y-1.5">
                            <li><strong>Access</strong> - download a copy of your data</li>
                            <li><strong>Deletion</strong> - request permanent deletion of your account and content</li>
                            <li><strong>Correction</strong> - update inaccurate information</li>
                            <li><strong>Portability</strong> - export your bookmarks in Markdown or JSON</li>
                            <li><strong>Opt-out</strong> - disable optional features (AI processing on save, public sharing)</li>
                        </ul>

                        <h3 className="text-xl font-semibold mt-6 mb-2">How to delete your BrainSync account</h3>
                        <p>You can request deletion of your account and all associated data using either method:</p>
                        <ol className="list-decimal pl-6 space-y-2 mt-2">
                            <li>
                                <strong>In-app (fastest)</strong>:
                                <ol className="list-[lower-alpha] pl-6 space-y-1 mt-1">
                                    <li>Sign in to BrainSync (web or Android).</li>
                                    <li>Tap your avatar in the top-right of the dashboard.</li>
                                    <li>Choose <em>Settings</em>.</li>
                                    <li>Scroll to the <em>Danger Zone</em> and tap <em>Delete my account</em>.</li>
                                    <li>Type your email to confirm. Deletion is immediate.</li>
                                </ol>
                            </li>
                            <li>
                                <strong>By email</strong>: Send a request from your account&apos;s registered email to{' '}
                                <a href="mailto:privacy@brainsync.pro" className="text-primary hover:underline">privacy@brainsync.pro</a>
                                {' '}with the subject &ldquo;Delete my account&rdquo;. We respond within 30 days as required by GDPR and CCPA.
                            </li>
                        </ol>

                        <h3 className="text-xl font-semibold mt-6 mb-2">What is deleted</h3>
                        <ul className="list-disc pl-6 space-y-1.5">
                            <li>Your profile, email address, password hash, and login credentials</li>
                            <li>All saved bookmarks, notes, collections, and tags</li>
                            <li>All AI chat history (per-bookmark and library-wide)</li>
                            <li>Reminders, in-app notifications, and personalised recommendations</li>
                            <li>Active sessions and device push tokens (you are signed out everywhere)</li>
                            <li>Email verification codes, login attempts, and password-reset tokens</li>
                            <li>Subscription record on our side (Stripe and RevenueCat continue to hold their own copies - cancel via Stripe to avoid future charges)</li>
                        </ul>

                        <h3 className="text-xl font-semibold mt-6 mb-2">What is kept (and why)</h3>
                        <ul className="list-disc pl-6 space-y-1.5">
                            <li>
                                <strong>Payment-transaction records</strong> are <em>anonymised</em> (your user ID is replaced with &ldquo;deleted&rdquo;)
                                but retained for up to 7 years to satisfy tax, accounting, and refund-audit obligations. Stripe and RevenueCat are
                                the systems of record for financial data.
                            </li>
                            <li>
                                <strong>Aggregated, non-identifying analytics</strong> (e.g. &ldquo;number of bookmarks saved this month&rdquo;) may
                                remain in our metrics - these contain no personal data.
                            </li>
                            <li>
                                <strong>Server backups</strong> are rotated within 30 days; any residual copies of your data are purged on that cycle.
                            </li>
                        </ul>

                        <h3 className="text-xl font-semibold mt-6 mb-2">Partial data deletion (without deleting your account)</h3>
                        <p>
                            You don&apos;t have to delete your whole account to remove specific data. From any bookmark detail page you can delete
                            individual bookmarks, notes, AI chat threads, or collections. Deleted items are removed immediately and are not recoverable.
                        </p>
                    </section>

                    <section>
                        <h2 className="text-2xl font-semibold mt-8 mb-3">8. Children's Privacy</h2>
                        <p>
                            BrainSync is not directed at children under 13 (or 16 in the EU). We do not knowingly collect personal
                            information from children. If you believe a child has provided us with information, please contact us
                            and we will delete it.
                        </p>
                    </section>

                    <section>
                        <h2 className="text-2xl font-semibold mt-8 mb-3">9. Security</h2>
                        <p>
                            We use industry-standard measures to protect your information: TLS 1.3 in transit, encryption at rest
                            on our database and object storage, scoped session cookies (HttpOnly, Secure, SameSite=Lax),
                            rate-limited APIs, and audit logging of administrative actions. No system is perfectly secure; if you
                            discover a vulnerability, please email{' '}
                            <a href="mailto:security@brainsync.pro" className="text-primary hover:underline">security@brainsync.pro</a>.
                        </p>
                    </section>

                    <section>
                        <h2 className="text-2xl font-semibold mt-8 mb-3">10. International Transfers</h2>
                        <p>
                            BrainSync is operated from infrastructure located in the United States and the European Union.
                            By using the Service, you consent to transfer of your data across borders for the purposes described
                            in this policy.
                        </p>
                    </section>

                    <section>
                        <h2 className="text-2xl font-semibold mt-8 mb-3">11. Changes to This Policy</h2>
                        <p>
                            We may update this Privacy Policy from time to time. If we make material changes, we will notify you
                            via email or an in-app banner before they take effect. The current version is always available at
                            <a href="https://brainsync.pro/privacy" className="text-primary hover:underline"> brainsync.pro/privacy</a>.
                        </p>
                    </section>

                    <section>
                        <h2 className="text-2xl font-semibold mt-8 mb-3">12. Contact</h2>
                        <p>
                            Questions, complaints, or requests:{' '}
                            <a href="mailto:privacy@brainsync.pro" className="text-primary hover:underline">privacy@brainsync.pro</a>
                        </p>
                        <p className="text-sm text-muted-foreground mt-3" data-testid="privacy-operator-line">
                            brainsync.pro is operated by <strong>Aniral Ltd</strong>, Tel Aviv, Israel.
                        </p>
                    </section>
                </div>

                <div className="mt-12 pt-6 border-t border-border text-center">
                    <Link
                        to="/"
                        className="text-sm text-muted-foreground hover:text-foreground transition-colors"
                        data-testid="privacy-home-link"
                    >
                        ← Back to BrainSync
                    </Link>
                </div>
            </main>
        </div>
    );
}
